/**
 * Flat canvas JSON → hierarchical IR tree.
 *
 * Step 3 of the IR build order (see docs/IR_RULE_MAPPING.md §7 step 3).
 * The canvas JSON is a `{nodes, connections}` DAG where nodes carry canvas
 * types (`condition`, `parallel_fork`, `loop`, `agent`, `trigger`, `end`,
 * plus leaf sub-types), and connections carry `sourceHandle` to disambiguate
 * switch case labels / fork branches / loop_back.
 *
 * The IR tree is the shape defined in types.js. This module walks the flat
 * graph from the Start sentinel and recursively assembles containers.
 *
 * ALGORITHM
 * ─────────
 * 1. Index: id→node, outgoing edges per source, incoming edges per target.
 * 2. Find Start (node with no incoming, or type=trigger/start).
 * 3. `walkSequence(from, stopAt)`: follow the default out-edge chain until
 *    we hit `stopAt` (a barrier id) or a container. Accumulate IR nodes.
 * 4. At a container:
 *    - `condition` (Switch): group outgoing by sourceHandle; each group is
 *      a Case. Recurse into each case up to the switch's post-dominator.
 *    - `parallel_fork` (Fork / DynamicFork): each branch_N outgoing is a
 *      Branch; the paired `parallel_join` is the barrier. JOIN is absorbed
 *      into Fork.join.
 *    - `loop` (DoWhile): default out-edge starts the body; body recurses
 *      until it returns to the loop via sourceHandle="loop_back". The loop's
 *      exit is the `exit` sourceHandle edge.
 *    - `agent` (SubWorkflow): scalar — emit a SubWorkflow IR node with
 *      `async` flag from config; continue sequence.
 *    - `end`: depends on context. If it's the trunk tail (last node reached
 *      from the root-level sequence), it's the End sentinel; inside a
 *      branch/case with no path back to the container's convergence point,
 *      it's a Terminate.
 * 5. Post-dominator detection: nearest common descendant of all case/branch
 *    exits. We compute this per container by DFS + intersection of
 *    descendant sets, ignoring `loop_back` edges (which form cycles).
 *
 * See: docs/IR_RULE_MAPPING.md §2 (IR sketch), §3 (rule-by-rule mapping).
 */

import {
  IR_KIND,
  CANVAS_TYPE,
  normalizeCanvasType,
  isSwitchType,
  isForkType,
  isJoinType,
  isForkJoinType,
  isLoopType,
  isSubWorkflowType,
  isTerminateType,
  isStartType,
  isTriggerCapableType,
  isEndType,
  isLoopBodyHandle,
  isLoopExitHandle,
  LOOP_BODY_HANDLE,
  LOOP_EXIT_HANDLE,
} from './kinds';

import {
  makeLeaf,
  makeStart,
  makeEnd,
  makeTerminate,
  makeSwitch,
  makeCase,
  makeFork,
  makeDynamicFork,
  makeBranch,
  makeJoin,
  makeDoWhile,
  makeSubWorkflow,
} from './types';

import { normalizeCanvasPayload } from './canvasPayloadNormalizer';

// ───────────────────────────────────────────────────────────────────
//  Public entry point
// ───────────────────────────────────────────────────────────────────

/**
 * Convert a flat canvas JSON document to a hierarchical IR tree.
 *
 * Expected input shape: `{ nodes: Node[], connections: Connection[], ... }`
 * where:
 *   Node       = { id, type, label, config, x?, y? }
 *   Connection = { source, target, sourceHandle?, style? }
 *
 * The returned IR is wrapped in a single "root" container — for now we
 * return the Start sentinel's first child's IR if the whole workflow is a
 * single container; otherwise we return an implicit sequence (array) via
 * a DoWhile-less wrapper. Callers use `walkIR` to enumerate.
 *
 * Because the IR has no explicit Sequence node, a linear workflow is
 * returned as an array of IRNodes (the top-level sequence). A single-node
 * workflow returns an array of length 1. This matches §2's "Sequence is
 * implicit" design — the root is always `IRNode[]`.
 *
 * @param {{ nodes: Array, connections: Array }} canvas
 * @returns {{ start: object, sequence: Array, end: object|null }}
 *          A wrapper object: `start` is the Start sentinel, `sequence` is
 *          the top-level body (array of IRNodes), `end` is the End sentinel
 *          if present, else null.
 */
export function flatToIR(canvas) {
  if (!canvas || !Array.isArray(canvas.nodes) || !Array.isArray(canvas.connections)) {
    throw new Error('flatToIR: expected { nodes: [], connections: [] }');
  }

  // G459 — Run the payload through the canonicalization normalizer
  // BEFORE building the IR context. This strips outbound edges from
  // terminate/end sentinel nodes (and similar pre-IR repairs) so the
  // walker operates on a clean structure. See canvasPayloadNormalizer.js
  // for the layered architecture (source emission → load normalization
  // → walker accommodations).
  const normalized = normalizeCanvasPayload(canvas);
  const cleanCanvas = normalized.payload;

  const ctx = buildContext(cleanCanvas);
  const startNode = findStart(ctx);
  if (!startNode) {
    throw new Error('flatToIR: no Start sentinel (no trigger/start node and no root-only node)');
  }

  const start = makeStart({ id: startNode.id });

  // ── The entry hop ────────────────────────────────────────────────
  //
  // Follow the Start's structural out-edges into the top-level sequence.
  //
  // Every other node in the graph gets this from walkSequence: one
  // successor continues the trunk, several are a parallel fan-out that
  // G458 renders as a Fork. The entry sentinel was the single exception.
  // It asked `singleDefaultSuccessor`, which takes the first `default`
  // out-edge in array order and discards the rest, and the G458 failsafe
  // could never cover it because `detectImplicitFanOut` returns null for
  // start types ("the start is emitted by the top-level walker").
  //
  // The cost was not cosmetic. A Start wired to two or more nodes rendered
  // whichever branch happened to sit first in `connections[]` — so the same
  // saved workflow rendered differently after a JSONB round trip, an AI
  // edit or a merge, and every node on the losing branch vanished from the
  // canvas while remaining in the payload the Code panel shows. The
  // operator sees a workflow that "breaks" and "unbreaks" with no edit in
  // between, which is how the DISCONNECTED reports presented.
  //
  // DAG_DESIGN_GUIDELINES §12 makes the Start a pure-display, 80×80,
  // non-interactive circle, so the fan-out must not become chrome on the
  // Start itself. It does not: `buildImplicitFork` emits its Fork as a
  // separate node positioned after its source, which is the shape §6
  // prescribes — branches spread below the Start, trunk resuming through
  // the centre at the convergence point. The Start's own rendering is
  // untouched.
  const entryOuts = structuralOuts(ctx, startNode.id);
  let sequence = [];
  let endSentinel = null;
  let trunkHead = null;

  if (entryOuts.length > 1) {
    // buildContext guarantees both endpoints of every indexed edge exist,
    // so each arm head resolves.
    const { fork, nextCursor } = buildImplicitFork(ctx, startNode, entryOuts, /* outerStopAt */ null);
    if (fork) sequence.push(fork);
    trunkHead = nextCursor;
  } else if (entryOuts.length === 1) {
    // Identical to the pre-existing `singleDefaultSuccessor` answer: with
    // exactly one non-auxiliary out-edge, both the "prefer default" branch
    // and the "unique non-auxiliary" fallback resolve to this same node.
    trunkHead = entryOuts[0].target;
  }

  if (trunkHead) {
    const { items, terminatedAt } = walkSequence(ctx, trunkHead, /* stopAt */ null, /* exitPredicate */ isEndSentinelCandidate);
    sequence = sequence.concat(items);
    if (terminatedAt && isEndSentinelCandidate(terminatedAt, ctx)) {
      endSentinel = makeEnd({ id: terminatedAt.id });
    }
  }

  // ── Post-build cycle-guard report ─────────────────────────────────
  // If the descent guard tripped at any point during materialization,
  // log a single warning with the offending node ids so operators can
  // pinpoint which upstream materializer emitted the back-edge. The IR
  // itself is still valid (the cycle nodes degraded to labelled Leaves)
  // and renders cleanly — this is purely diagnostic.
  if (ctx.cycleGuardHits.length > 0) {
    const unique = Array.from(new Set(ctx.cycleGuardHits));
    // eslint-disable-next-line no-console
    console.warn(
      '[flatToIR] cycle/depth guard tripped on ' + unique.length +
      ' node(s): ' + unique.slice(0, 8).join(', ') +
      (unique.length > 8 ? ` (+${unique.length - 8} more)` : '') +
      '. These nodes are rendered as labelled stubs; the rest of the ' +
      'layout is intact. Investigate the upstream materializer that ' +
      'emitted a back-edge into the DAG.'
    );
  }

  return { start, sequence, end: endSentinel };
}

// ───────────────────────────────────────────────────────────────────
//  Context: indexed view of the flat graph
// ───────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} Ctx
 * @property {Map<string, object>} nodes        id → node
 * @property {Map<string, Array>}  outgoing     id → connection[]
 * @property {Map<string, Array>}  incoming     id → connection[]
 * @property {Array} connections                all connections (stable order)
 */

function buildContext(canvas) {
  /** @type {Ctx} */
  const ctx = {
    nodes: new Map(),
    outgoing: new Map(),
    incoming: new Map(),
    connections: canvas.connections,
    // ── Recursion guards (#37) ────────────────────────────────────────
    // walkSequence has a per-call `visited` set, but switch/fork builders
    // recurse through walkSequence per arm — so a nested switch whose
    // case head reaches back into an enclosing branch can re-enter
    // materializeNode on the same node id with a fresh per-call visited
    // and recurse mutual-tail-style until the JS stack overflows ("Layout
    // error: Maximum call stack size exceeded"). This SHARED descent set
    // tracks which canvas-node ids are currently being materialized; if
    // we hit a node already on the descent we degrade to a Leaf with a
    // diagnostic and stop descending. Mirrors the emitIRNode guard in
    // flatToElkHierarchical.GraphBuilder.
    descent: new Set(),
    // Hard ceiling — defends against pathological deep chains where every
    // id differs but the recursion never converges. 256 is ~5x deeper
    // than any well-formed workflow we've ever seen.
    descentDepth: 0,
    descentDepthMax: 256,
    // Diagnostic: nodes that triggered the cycle guard. Surfaced in a
    // console.warn after the build completes so operators can identify
    // exactly which node (and therefore which materializer bug) is
    // emitting a back-edge into the DAG.
    cycleGuardHits: [],
    // G455 — Tracks parallel_join node ids that have been "claimed" as the
    // paired join of a Fork (or DynamicFork). When buildFork resolves its
    // paired join via findPairedJoin, it adds the join id here so that
    // materializeNode can distinguish:
    //   • Fork-paired joins   → return null  (absorbed into the Fork IR)
    //   • Standalone joins    → return buildLeaf(...)  (emitted as a leaf)
    //
    // LAYERED ARCHITECTURE (defense in depth):
    //
    //   Layer 1 — Source emission (PRIMARY):
    //     CanvasAIService.WORKFLOW_BUILDER_SYSTEM_PROMPT Rule 13 forbids
    //     standalone parallel_joins. validateAndRepairDAG Phase 4c warns
    //     on detection.
    //
    //   Layer 2 — Load-time normalization (canvasPayloadNormalizer.js):
    //     Auto-repairs known malformed shapes before flatToIR sees them.
    //
    //   Layer 3 — THIS WALKER ACCOMMODATION (failsafe):
    //     If somehow a standalone parallel_join slips through Layers 1+2
    //     (legacy import, hand-author bypass, bugged migration), the
    //     walker emits it as a regular leaf instead of silently dropping
    //     it. The leaf renders correctly via ELK; switch arms that
    //     converge at the join have their convergence preserved.
    //
    // Pre-G455, materializeNode dropped EVERY parallel_join unconditionally
    // ("absorbed into the parent Fork"), which silently broke workflows
    // where a parallel_join was used as the convergence point for two
    // switch arms (no Fork to absorb into). Those joins were dropped from
    // the IR entirely — the downstream layout never saw them, the switch
    // arms lost their convergence target, and W133 (V2CanvasView) had to
    // surface them as Disconnected after-the-fact.
    claimedJoins: new Set(),
    // G483 — Tracks every canvas-source node id that has already been
    // emitted into the IR tree. Prevents duplicate emission when multiple
    // walk paths converge on the same downstream subtree.
    //
    // Concrete trigger (IdentityOnboadring v4): route_event's HIRE case
    // contains approval_check whose .true arm has postDom=null (because
    // .false terminates). That arm's walkSequence runs with stopAt=null
    // and consumes parallel_fork → parallel_join → audit_log →
    // notify_team → end into approval_check.true's case children.
    //
    // Meanwhile, the OUTER walk (at validate_check.true level) calls
    // advanceAfter(route_event_switch) which returns parallel_join
    // (route_event's postDom). The outer walk continues past parallel_join
    // (claimedJoins makes materializeNode return null for it) and
    // re-emits audit_log / notify_team / end at trunk level — duplicating
    // them in the IR. ELK then positions BOTH copies, producing visible
    // scatter and stranded clusters.
    //
    // Fix: materializeNode short-circuits to null when a node has already
    // been emitted. The walker still advances past the node (so the trunk
    // continues correctly), but it doesn't push a duplicate IR node.
    materialized: new Set(),
    // Nodes an authoritative container hint has already assigned to a
    // container, mapped to the container that owns them. See
    // `bodyOwner` in buildContext and the reservation check in
    // materializeNode.
    bodyOwner: new Map(),
    // Set to a loop's id while that loop's body is being walked, so the
    // reservation check can tell "this is the owner collecting its own
    // body" from "somebody else got here first".
    buildingBodyFor: null,
    // Distinguishes successive cycle stubs. A stub carries a label, not an
    // identity, so it must not reuse the id of the node it stands for —
    // that node is already emitted somewhere else in the tree.
    cycleStubSeq: 0,
  };
  for (const n of canvas.nodes) {
    if (!n || typeof n !== 'object' || !n.id) {
      throw new Error(`flatToIR: node missing id: ${JSON.stringify(n)}`);
    }
    ctx.nodes.set(n.id, n);
    ctx.outgoing.set(n.id, []);
    ctx.incoming.set(n.id, []);
    /* A loop that carries an explicit `_loopBody` is stating which nodes
     * belong inside it, and buildDoWhile already treats that as
     * authoritative. But "authoritative" only held if the loop got there
     * first: whichever walk reached a body node earliest emitted it, and
     * the G483 dedupe then returned null for every later claim — including
     * the loop's own. The body rendered empty and its task appeared
     * somewhere it does not belong.
     *
     * Recording the claim up front makes the outcome independent of which
     * walk runs first, which is the same property the whole C1 family is
     * about. Mirrors claimedJoins, where a fork reserves its paired join
     * so no other path emits it as a stray leaf. */
    if (Array.isArray(n._loopBody)) {
      for (const bodyId of n._loopBody) {
        if (typeof bodyId === 'string' && bodyId && !ctx.bodyOwner.has(bodyId)) {
          ctx.bodyOwner.set(bodyId, n.id);
        }
      }
    }
  }
  // Stripped on purpose: edges originating at end / terminate sentinels.
  // An "end" node by definition terminates the workflow — any outgoing
  // edge from one is semantically nonsensical and is almost always
  // either canvas corruption or a stray feedback edge added by some
  // upstream normalizer. We've seen GEL enforcement DAGs ship with a
  // spurious "Execution Complete → Conductor OSS Webhook" edge that closes a
  // structural cycle through the entire DAG; flatToIR.walkSequence
  // doesn't recurse forever (it has a per-call visited Set), but the
  // resulting IR contains the same node id reachable via two paths,
  // which then trips emitIRNode's descent-stack guard and produces
  // the "(⚠ circular reference)" stub. Filtering at the edge level
  // here means the IR stays clean regardless of upstream sloppiness.
  let droppedEnd = 0;
  for (const c of canvas.connections) {
    // Skip dangling / rendering-only edges — the canvas JSON commonly carries
    // dashed-line terminate markers with `target: null`. These are not
    // structural flow edges; ignoring them keeps the graph walk clean.
    if (!c || !c.source || !c.target) continue;
    if (!ctx.nodes.has(c.source) || !ctx.nodes.has(c.target)) {
      // Reference to a node not in the node list — surface rather than drop
      // silently, since this signals canvas corruption.
      throw new Error(`flatToIR: connection references unknown node(s): ${c.source} → ${c.target}`);
    }
    const sourceNode = ctx.nodes.get(c.source);
    const sourceType = sourceNode && sourceNode.type;
    if (isTerminateType(sourceType) || isEndType(sourceType)) {
      droppedEnd += 1;
      continue;
    }
    ctx.outgoing.get(c.source).push(c);
    ctx.incoming.get(c.target).push(c);
  }
  if (droppedEnd > 0) {
    // eslint-disable-next-line no-console
    console.warn('[flatToIR] dropped ' + droppedEnd + ' outgoing edge(s) from end/terminate nodes — '
      + 'these are structurally invalid (sentinels have no successors). '
      + 'Likely an upstream normalizer added a feedback edge; the IR is rendered without them.');
  }
  return ctx;
}

function findStart(ctx) {
  // Tiered selection — fall through layers until exactly one candidate
  // emerges. Designed to be resilient to AI-Builder edits that swap
  // the canonical manual_start for a different trigger type
  // (form_trigger, webhook, schedule, …) without manually rewiring
  // the start sentinel; previously this path threw "no Start sentinel"
  // and blanked the canvas mid-edit.
  //
  //   Tier 1: explicit START-typed node (manual_start / trigger / start)
  //   Tier 2: trigger-capable node with no incoming edges
  //           (webhook / schedule / form_trigger / etc. used AS the entry)
  //   Tier 3: any node with no incoming edges
  //
  // Ambiguous tiers (multiple candidates within a tier) drop to the
  // next tier rather than throwing — the user can still see and edit
  // their graph while the validator surfaces the multi-entry problem
  // separately. Throw only when no tier produces a single candidate.

  // ── Tier 1: explicit START-typed sentinel ───────────────────────
  const explicitStarts = [];
  for (const n of ctx.nodes.values()) {
    if (isStartType(n.type)) explicitStarts.push(n);
  }
  if (explicitStarts.length === 1) return explicitStarts[0];
  if (explicitStarts.length > 1) {
    /* G292 — Multiple Start sentinels used to throw and blank the
     * canvas, leaving operators unable to see their workflow at all.
     * The most common cause is the G256 operator-node-preservation
     * merge layering a freshly-materialised `manual_start` over a
     * legacy `start` carried forward from a pre-G238 workflow record.
     * Both are valid sentinels; the modern materialiser emits
     * `manual_start`, so prefer it when present. Among ties (e.g.
     * two `manual_start` from a botched re-materialise), pick the one
     * with the most outgoing edges — that's the "real" entry feeding
     * the rest of the DAG. The losing candidates render as orphans
     * the operator can delete (G268 diff modal surfaces them).
     *
     * Mirrors the multi-trigger heuristic in Tier 2 below.
     */
    const ranked = [...explicitStarts].sort((a, b) => {
      const aManual = a.type === 'manual_start' ? 1 : 0;
      const bManual = b.type === 'manual_start' ? 1 : 0;
      if (aManual !== bManual) return bManual - aManual; // manual_start first
      const aOut = (ctx.outgoing.get(a.id) || []).length;
      const bOut = (ctx.outgoing.get(b.id) || []).length;
      return bOut - aOut; // most outgoing first
    });
    // eslint-disable-next-line no-console
    console.warn(
      '[flatToIR] multiple Start sentinels found: ' +
      explicitStarts.map((n) => `${n.id}(${n.type})`).join(', ') +
      '. Using "' + (ranked[0].label || ranked[0].id) +
      '" (type=' + ranked[0].type + ', most outgoing edges). ' +
      'The other sentinels render as orphans — delete them in the canvas to clean up.',
    );
    return ranked[0];
  }

  // ── Tier 2/3 (RC-2, task #179): synthesize a virtual Start above the
  // first authored node — NEVER consume an authored trigger-capable or
  // generic root node as the Start sentinel.
  //
  // Background: pre-RC-2, this function picked a trigger-capable node
  // (webhook/schedule/form_trigger/etc.) or a generic root node and
  // returned it as the IR Start. flatToElkHierarchical.emitStart then
  // rendered that node as the round green Start circle, hiding the
  // authored node's card view and config controls from the canvas.
  //
  // The operator-facing consequence: a workflow whose trigger is a
  // webhook had no visible "webhook" card on the canvas — operators
  // couldn't see the path, method, or inputParameters of their own
  // trigger; the trigger's identity was concealed behind a green
  // circle labelled "START".
  //
  // The corrected design: START is a pure visual sentinel, always.
  // When no explicit Start sentinel exists in the canvas, we synthesize
  // a virtual one (id = `__virtual_start__`) that the IR walker uses
  // as the entry point and the renderer draws as a plain "Start" circle.
  // The trigger/root then becomes a regular leaf in the sequence and
  // renders as its own authored card.
  //
  // ctx mutation: we add the virtual node to `ctx.nodes` and a virtual
  // outgoing edge in `ctx.outgoing`. We deliberately do NOT add a
  // matching `ctx.incoming` entry on the root — downstream root-detection
  // (e.g. for IR validation) should still see the root as a true root,
  // because the virtual edge is a rendering convenience, not a structural
  // dependency of the authored DAG.
  return synthesizeVirtualStart(ctx);
}

/** Preferred id for the synthesized virtual Start node. */
const VIRTUAL_START_ID = '__virtual_start__';

/**
 * Claim an id for a node the walker synthesizes, without displacing an
 * authored one.
 *
 * The walker invents three kinds of node that never existed in the payload:
 * the virtual Start, and the fork and join that wrap an implicit fan-out.
 * Each wrote its id into `ctx.nodes` with `set()` and no existence check, so
 * a canvas that happened to contain a node with that id lost it — not just
 * its label and type, but its edges too, because the same three `set()` calls
 * overwrite `ctx.outgoing` and `ctx.incoming`. Everything downstream of that
 * node went with it, silently, while staying in the saved JSON.
 *
 * Those ids are not exotic enough to rely on never colliding. They are
 * derived from the canvas — `__implicit_fork__${sourceId}` — so a workflow
 * that has been through an export, an edit and a re-import can carry one as
 * an authored id. And `__virtual_start__` is a literal an AI generator or a
 * hand-authoring operator can produce by accident.
 *
 * The authored node always wins: it is the operator's data, and the
 * synthesized one is ours to rename. The suffix is derived only from the
 * authored node set, which does not change when `connections[]` is reordered,
 * so the result stays deterministic under the P1 permutation invariant and
 * the P3 determinism one.
 *
 * Nothing outside this module matches these ids as strings — the Start
 * renders from its kind, and v2Lint anchors its diagnostics to authored
 * payload nodes — so a suffixed id reaches no operator-facing surface.
 */
function reserveSyntheticId(ctx, preferred) {
  if (!ctx.nodes.has(preferred)) return preferred;
  let n = 2;
  while (ctx.nodes.has(`${preferred}__${n}`)) n += 1;
  return `${preferred}__${n}`;
}

/**
 * Build a synthetic Start vertex above the first authored root, leaving
 * the root in the canvas-walker's sequence so it renders as a regular
 * card. Mutates `ctx.nodes` and `ctx.outgoing` (only) to add the
 * virtual entry-point.
 *
 * Root selection (ranked):
 *   1. The unique trigger-capable root (webhook/schedule/form_trigger/etc.).
 *   2. If multiple trigger-capable roots: the one with the most outgoing
 *      edges (heuristic: it feeds the most downstream work). Others
 *      render as orphans — operators see them on canvas and can rewire.
 *   3. The unique generic root (no incoming, any type).
 *   4. Multiple generic roots: most-outgoing wins; rest are orphans.
 *
 * Returns the virtual Start node, or {@code null} when the canvas has
 * no root at all (caller throws a clean banner-friendly error).
 */
function synthesizeVirtualStart(ctx) {
  const noIncoming = [...ctx.nodes.values()].filter(
    (n) => (ctx.incoming.get(n.id) || []).length === 0,
  );
  if (noIncoming.length === 0) return null;

  const triggerRoots = noIncoming.filter((n) => isTriggerCapableType(n.type));
  let chosenRoot;
  if (triggerRoots.length === 1) {
    chosenRoot = triggerRoots[0];
  } else if (triggerRoots.length > 1) {
    triggerRoots.sort(
      (a, b) => (ctx.outgoing.get(b.id) || []).length - (ctx.outgoing.get(a.id) || []).length,
    );
    // eslint-disable-next-line no-console
    console.warn(
      '[flatToIR] multiple trigger-capable roots: ' +
      triggerRoots.map((n) => n.id).join(', ') +
      '. Synthetic Start connects to "' + (triggerRoots[0].label || triggerRoots[0].id) +
      '" (most downstream connections). Other trigger roots render as orphans the operator can rewire.',
    );
    chosenRoot = triggerRoots[0];
  } else {
    // No trigger-capable root — fall back to the canonical root by
    // most-outgoing tiebreak.
    if (noIncoming.length > 1) {
      noIncoming.sort(
        (a, b) => (ctx.outgoing.get(b.id) || []).length - (ctx.outgoing.get(a.id) || []).length,
      );
      // eslint-disable-next-line no-console
      console.warn(
        '[flatToIR] multiple generic roots (no trigger-capable): ' +
        noIncoming.map((n) => n.id).join(', ') +
        '. Synthetic Start connects to "' + (noIncoming[0].label || noIncoming[0].id) + '".',
      );
    }
    chosenRoot = noIncoming[0];
  }

  // Materialise the virtual node into ctx so the walker can traverse it.
  // Marked `virtual: true` so consumers (irToFlat, validators, etc.) can
  // strip it from any serialization round-trip.
  // Never displace an authored node that happens to carry this id — see
  // reserveSyntheticId. Resolved before the node is built so every reference
  // below uses the id we actually claimed.
  const startId = reserveSyntheticId(ctx, VIRTUAL_START_ID);
  const virtualStart = {
    id: startId,
    type: 'start',
    label: 'Start',
    virtual: true,
  };
  ctx.nodes.set(startId, virtualStart);
  ctx.outgoing.set(startId, [{
    source: startId,
    target: chosenRoot.id,
    sourceHandle: 'default',
    virtual: true,
  }]);
  ctx.incoming.set(startId, []);
  // Intentionally NOT pushing to ctx.incoming.get(chosenRoot.id) — see
  // comment at the call site for why root-detection must still treat
  // chosenRoot as a true root structurally.

  return virtualStart;
}

// ───────────────────────────────────────────────────────────────────
//  Sequence walker
// ───────────────────────────────────────────────────────────────────

/**
 * Walk forward from `startId` along default out-edges, converting each
 * visited node into an IR node. Stop when we hit:
 *   - `stopAt` (a barrier node id set by the caller for branch/case ends),
 *   - a node where `exitPredicate(node, ctx)` returns true (trunk's End
 *     sentinel),
 *   - a node that's a Terminate / End (dead-end for this branch),
 *   - a node with no default successor (branch ends without reaching the
 *     barrier — treated as a Terminate if the node is `end`-typed, or as a
 *     loop_back link if the edge is loop_back).
 *
 * @param {Ctx} ctx
 * @param {string} startId
 * @param {string | null} stopAt   Barrier node id (exclusive); walk stops BEFORE it.
 * @param {(n: object, ctx: Ctx) => boolean} [exitPredicate]
 * @returns {{ items: Array, terminatedAt: object | null }}
 */
function walkSequence(ctx, startId, stopAt, exitPredicate) {
  const items = [];
  let cursorId = startId;
  let terminatedAt = null;
  const visited = new Set();

  while (cursorId) {
    if (stopAt && cursorId === stopAt) {
      terminatedAt = ctx.nodes.get(cursorId);
      break;
    }
    if (visited.has(cursorId)) {
      // Cycle — e.g. loop_back to container head; stop here.
      break;
    }
    visited.add(cursorId);

    const node = ctx.nodes.get(cursorId);
    if (!node) throw new Error(`flatToIR: missing node ${cursorId}`);

    // End sentinel: end of trunk.
    if (exitPredicate && exitPredicate(node, ctx)) {
      terminatedAt = node;
      break;
    }

    // Dispatch by kind. Thread the walk's stopAt (the enclosing barrier)
    // so a nested switch whose own arm terminates can inherit it — see
    // buildSwitch's outerStopAt fallback (G485).
    const irNode = materializeNode(ctx, node, stopAt);
    if (irNode) items.push(irNode);

    // G458 — Implicit parallel fan-out detection (FAILSAFE).
    //
    // A non-container node (typically an http_task, data_transform, etc.)
    // with MULTIPLE outgoing edges on the `default` sourceHandle is an
    // AI-generator / legacy-converter anti-pattern: the node fans out to
    // multiple parallel paths without being wrapped in a parallel_fork.
    //
    // LAYERED ARCHITECTURE (defense in depth):
    //
    //   Layer 1 — Source emission (PRIMARY):
    //     CanvasAIService.WORKFLOW_BUILDER_SYSTEM_PROMPT Rule 12 forbids
    //     this pattern. validateAndRepairDAG Phase 4b warns on detection.
    //
    //   Layer 2 — Load-time normalization (canvasPayloadNormalizer.js):
    //     Future enhancement (TODO): auto-synthesize an explicit
    //     parallel_fork + parallel_join wrapper. Currently no-op for
    //     this pattern; relies on Layer 3.
    //
    //   Layer 3 — THIS WALKER ACCOMMODATION (failsafe):
    //     If a multi-default-out node slips through Layers 1+2 (legacy
    //     import, hand-author bypass), the walker synthesizes a Fork IR
    //     with the node's parallel paths as branches, finds the
    //     convergence postDom, and advances the cursor past the postDom.
    //     If the postDom is a parallel_join, claim it (same machinery as
    //     buildFork per G455) so it's absorbed into the synthetic fork
    //     chrome rather than rendering as a duplicate leaf afterwards.
    //
    // Pre-G458, `singleDefaultSuccessor` silently picked the FIRST
    // default-handle outbound and dropped all sibling paths from the IR.
    // Repro: IdentityOnboadring v4 LEAVE arm, where
    // n_obtain_sailpoint_token_leave fanned out to BOTH
    // n_scim_suspend_user AND n_scim_reactivate_user — the latter chain
    // was completely dropped.
    const fanOut = detectImplicitFanOut(ctx, node);
    if (fanOut) {
      const result = buildImplicitFork(ctx, node, fanOut, stopAt);
      if (result.fork) items.push(result.fork);
      cursorId = result.nextCursor;
      if (!cursorId) {
        terminatedAt = node;
        break;
      }
      continue;
    }

    // Advance: follow the default successor, or the container's post-exit.
    const next = advanceAfter(ctx, node);
    cursorId = next ? next.id : null;
    if (!cursorId) {
      // Dead end — node had no default out-edge.
      terminatedAt = node;
      break;
    }
  }

  return { items, terminatedAt };
}

/**
 * G458 — Detect an implicit parallel fan-out.
 *
 * Returns the list of default-handle outbound edges when the node has
 * more than one and the node ISN'T a container that handles multi-out
 * via its own semantics (fork → branch_*, switch → case-name handles,
 * do_while → exit/loop_back). Returns null otherwise (normal trunk
 * continuation handled by advanceAfter / singleDefaultSuccessor).
 */
function detectImplicitFanOut(ctx, node) {
  const t = normalizeCanvasType(node.type);
  // A gate owns its own multi-out semantics too, and it is NOT parallel —
  // materializeNode already emitted it as a Switch whose cases are these
  // arms. Detecting a fan-out here as well would emit them twice.
  if (detectGateArms(ctx, node)) return null;
  // Container types own their multi-out semantics — never treat them
  // as implicit fan-outs (we'd double-emit their branches).
  if (isSwitchType(t)) return null;
  if (isForkType(t)) return null;
  if (isLoopType(t)) return null;
  // Start / terminate / end sentinels: shouldn't have multi-out, and
  // even if they do, the start is emitted by the top-level walker and
  // terminates have no successors of interest.
  if (isStartType(t)) return null;
  if (isTerminateType(t)) return null;

  const outs = ctx.outgoing.get(node.id) || [];
  const defaultOuts = outs.filter((c) => {
    const h = c.sourceHandle || 'default';
    return h === 'default';
  });
  return defaultOuts.length > 1 ? defaultOuts : null;
}

/**
 * C1 — Detect a GATE: a non-container node whose flow leaves on two or more
 * DIFFERENT declared ports.
 *
 * Twenty registered types are shaped this way, and every one of them is a
 * conditional, not a fan-out — exactly one arm runs:
 *
 *   [default | blocked]              the ten policy / DPG / classification gates
 *   [default | timeout]              http_poll_task, signal_wait
 *   [approved | rejected | timeout]  approval_gate, form_task, human_task
 *   [approved | rejected]            dual_approval
 *   [success | error]                error_handler
 *   [authorized | denied]            rbac_gate
 *   [pass | fail]                    risk_score_gate
 *   [in_window | out_of_window]      time_window_gate
 *
 * `singleDefaultSuccessor` could route exactly one of them. A gate declaring
 * a `default` lost every other arm to `outs.find(...)`; a gate declaring no
 * `default` — half the list — fell through to the "exactly one non-auxiliary
 * out-edge" branch, so it routed while ONE arm was wired and returned null the
 * moment the second was. The workflow broke at the point its author finished
 * building it, and the nodes stayed in the saved JSON while vanishing from the
 * canvas.
 *
 * Two deliberate limits on when this fires:
 *
 *   - At least two DISTINCT handles. Two edges sharing one handle is the
 *     G458 implicit fan-out — genuinely parallel, and rendered as a Fork.
 *     A gate is the opposite claim about execution and must not borrow that
 *     shape.
 *   - Auxiliary handles are excluded, so the cognitive-loop availability and
 *     return edges and the do-while back-edge never make a node look like a
 *     gate. A cognitive_loop is a loop type and is rejected outright above
 *     that anyway.
 *
 * Returns the arm edges, or null when the node is not a gate. A gate with one
 * arm wired is not a gate yet: it stays a leaf on the trunk and renders
 * exactly as it does today.
 */
function detectGateArms(ctx, node) {
  const t = normalizeCanvasType(node.type);
  // Containers and sentinels own their own semantics.
  if (isSwitchType(t) || isForkType(t) || isLoopType(t) || isForkJoinType(t)) return null;
  if (isStartType(t) || isTerminateType(t) || isEndType(t)) return null;
  if (isSubWorkflowType(t)) return null;

  const outs = structuralOuts(ctx, node.id);
  if (outs.length < 2) return null;

  const handles = new Set(outs.map((c) => c.sourceHandle || 'default'));
  if (handles.size < 2) return null;

  return outs;
}

/**
 * G458 — Synthesize a Fork IR around an implicit parallel fan-out.
 *
 * Walks each out-edge's branch up to the common postDom of all branches
 * (or to the outer caller's stopAt if no postDom exists), builds a Fork IR
 * whose branches are those walks, and returns the cursor to advance to
 * next.
 *
 * TWO CALLERS, and they select `outs` differently on purpose:
 *
 *   walkSequence  passes the multiple `default`-handle edges of an ordinary
 *                 node (detectImplicitFanOut) — an anti-pattern the source
 *                 layers try to prevent, caught here as a failsafe.
 *   flatToIR      passes the Start's structural out-edges. A Start declares
 *                 one out-port and fires unconditionally, so several edges
 *                 leaving it are a parallel launch however they are
 *                 handled — there is no conditional reading of a trigger.
 *
 * The Fork is emitted as a node in its own right, positioned after its
 * source. It is never chrome ON the source, which is what lets the Start
 * stay the pure-display sentinel DAG_DESIGN_GUIDELINES §12 requires.
 *
 * If postDom is a parallel_join, we claim it (mirrors buildFork's
 * G455 behaviour) so materializeNode absorbs it into the synthetic
 * fork chrome instead of re-emitting it as a leaf afterwards. The
 * cursor then advances past the join via advanceAfter.
 *
 * If postDom is some other node type, we synthesize a virtual Join IR
 * to anchor the fork chrome and let the cursor land ON the postDom so
 * the main walk continues to render it as a normal leaf next iteration.
 */
function buildImplicitFork(ctx, sourceNode, outs, outerStopAt) {
  // Claimed up front so the fork and its join agree on the suffix even when
  // one of the two preferred ids is taken and the other is not.
  const forkId = reserveSyntheticId(ctx, `__implicit_fork__${sourceNode.id}`);
  const virtualJoinId = reserveSyntheticId(ctx, `__implicit_join__${sourceNode.id}`);
  const targets = outs.map((c) => c.target);
  const postDomId = computePostDominator(ctx, sourceNode.id, targets);
  const stopAtForBranches = postDomId || outerStopAt || null;

  const branches = outs.map((c, idx) => {
    const handle = `branch_${idx + 1}`;
    const { items } = walkSequence(ctx, c.target, stopAtForBranches, null);
    return makeBranch({ name: handle, children: items });
  });

  let join;
  let nextCursor;
  if (postDomId) {
    const postDomNode = ctx.nodes.get(postDomId);
    if (postDomNode && isForkJoinType(postDomNode.type)) {
      // Absorb the explicit parallel_join into this synthetic fork's
      // join slot (same contract as buildFork in G455). Claim it so
      // materializeNode returns null for it on the next iteration,
      // then advance the cursor PAST the join to its successor.
      ctx.claimedJoins.add(postDomId);
      const joinCfg = postDomNode.config || {};
      const waitMode =
        joinCfg.joinMode === 'ANY_COMPLETED' || joinCfg.joinMode === 'ANY'
          ? 'ANY'
          : 'ALL';
      join = makeJoin({
        id: postDomId,
        label: displayLabel(postDomNode) || 'Join',
        joinOn: branches.map((b) => b.name),
        waitMode,
      });
      // G482 — Respect outerStopAt when advancing past the absorbed join.
      //
      // Without this, an implicit fork whose absorbed join IS the outer
      // walk's stopAt boundary (e.g. when route_event's LEAVE case has an
      // implicit fan-out at obtain_sailpoint_token_leave that converges at
      // parallel_join — which is ALSO route_event's case-postDom) causes
      // the outer walkSequence to overshoot past its boundary.
      //
      // The mechanics: buildImplicitFork absorbs parallel_join via
      // claimedJoins, then sets nextCursor = advanceAfter(parallel_join) =
      // audit_log (the post-join node). Control returns to the outer
      // walkSequence with cursorId = audit_log. The stopAt check fires
      // only when cursorId === stopAt — but stopAt is parallel_join and
      // cursorId is audit_log, so the check fails. The walk continues
      // and audit_log, notify_team, etc. get sucked INTO the LEAVE case's
      // children. They render at top level in rfNodes (via root order),
      // but the IR has them inside a case — ELK can't reconcile that
      // contradiction and scatters them across the canvas.
      //
      // Fix: if the absorbed join is the outer stopAt, return nextCursor =
      // outerStopAt so walkSequence's stopAt check fires correctly on the
      // next iteration and the outer walk terminates at its boundary,
      // leaving audit_log + downstream as trunk-level peers of the
      // enclosing switch — where they belong.
      //
      // Repro: IdentityOnboadring v4. Pre-G482 the implicit_fork → audit_log
      // edge spanned 2090px across the canvas; audit_log + notify_team
      // rendered as a stranded cluster far below the route_event subtree.
      if (outerStopAt && postDomId === outerStopAt) {
        nextCursor = outerStopAt;
      } else {
        const after = advanceAfter(ctx, postDomNode);
        nextCursor = after ? after.id : null;
      }
    } else {
      // postDom isn't a parallel_join — synthesize a virtual Join just
      // for ELK chrome, and let the cursor LAND on postDom so the
      // outer walk continues to render it normally.
      join = makeJoin({
        id: virtualJoinId,
        label: 'Join',
        joinOn: branches.map((b) => b.name),
        waitMode: 'ALL',
      });
      nextCursor = postDomId;
    }
  } else {
    // No common convergence — branches diverge fully. Synthesize a
    // virtual Join slot anyway (Fork IR requires one), and terminate
    // the trunk after the fan-out.
    join = makeJoin({
      id: virtualJoinId,
      label: 'Join',
      joinOn: branches.map((b) => b.name),
      waitMode: 'ALL',
    });
    nextCursor = null;
  }

  // The operator did not place this Fork — the walk synthesized it because
  // several paths leave one node. So its label is the only thing on the
  // canvas that explains why the container is there, and it has to read as
  // guidance rather than as a marker left behind by the code. It renders in
  // two places: `Fork · <label> · N branches` on the container chip, and
  // under the `Fork · parallel` eyebrow on the head.
  //
  // A fixed string on purpose. Naming the source node here would fall back
  // to `displayLabel`, which yields the raw node id for an unlabelled node —
  // putting `manual_start_1` on an operator's canvas.
  const fork = makeFork({
    id: forkId,
    label: 'Runs in parallel',
    branches,
    join,
  });
  return { fork, nextCursor };
}

// ───────────────────────────────────────────────────────────────────
//  Node dispatch
// ───────────────────────────────────────────────────────────────────

function materializeNode(ctx, node, outerStopAt = null) {
  const t = normalizeCanvasType(node.type);

  // Skip node types that are STRUCTURAL barriers, not IR-emitting: Start
  // is emitted by the top-level.
  if (isStartType(t)) return null;

  // G483 — Dedup guard. If this node has already been emitted into the
  // IR via some other path (e.g. consumed into an inner switch's case
  // children because that case had a no-postDom converging arm that
  // walked past the outer container's postDom), return null here so the
  // outer walk doesn't emit a duplicate. The walker still calls
  // `advanceAfter` on this node and continues past it, so the trunk
  // continuation stays correct.
  //
  // Repro: IdentityOnboadring v4. route_event.HIRE case contains
  // approval_check; approval_check.true arm has postDom=null (because
  // .false terminates) so its walk runs forever and consumes
  // parallel_fork → parallel_join → audit_log → notify_team → end.
  // Meanwhile the validate_check.true outer walk (one level above
  // route_event) hits the same audit_log / notify_team / end after
  // advanceAfter(route_event_switch) and re-emits them at trunk level.
  // Pre-G483 those nodes rendered as duplicate rfNodes — ELK had to
  // position both copies, causing visible scatter.
  /* ── Cycle guard, BEFORE the dedupe ──────────────────────────────────
   *
   * These two checks were the other way round, and the order made the
   * cycle guard unreachable. `materialized.add` runs the moment a node
   * starts materializing and `descent.add` a few lines later, so anything
   * on the descent stack is necessarily already in `materialized` — the
   * dedupe below returned null first, every time. A back-edge was
   * therefore swallowed as if it were an ordinary convergence: no stub, no
   * warning, and a container arm rendered empty with nothing to say why.
   * The #37 guard had been dead code since G483 landed.
   *
   * The two sets answer different questions and only this order keeps them
   * distinct. `descent` means "we are inside materializing this node right
   * now", which can only be a structural cycle. `materialized` means
   * "already emitted somewhere else", which is an ordinary convergence and
   * should be deduped in silence. */
  if (ctx.descent.has(node.id)) {
    ctx.cycleGuardHits.push(node.id);
    return cycleGuardLeaf(ctx, node, 'circular');
  }

  /* ── Container reservation ───────────────────────────────────────────
   * A node an authoritative container hint assigned elsewhere is not ours
   * to emit. Skipping it here leaves it unmaterialized so its owner still
   * finds it, whichever walk happens to arrive first. */
  const owner = ctx.bodyOwner.get(node.id);
  if (owner && owner !== ctx.buildingBodyFor) return null;

  if (ctx.materialized.has(node.id)) return null;
  ctx.materialized.add(node.id);

  // G455 — parallel_join handling.
  //
  // Two legitimate uses of `parallel_join` exist in canvas payloads:
  //
  //   1. Paired with a `parallel_fork`. buildFork claims the join via
  //      findPairedJoin() and absorbs it into the Fork IR's `join` slot
  //      (so it renders as the fork's "tail" container chrome, not as a
  //      separate leaf). For these, return null — already accounted for.
  //
  //   2. Standalone, as the convergence target for two-or-more arbitrary
  //      paths (e.g. an AI generator emits a parallel_join as the
  //      post-dominator of a switch's case arms, or to merge a manual
  //      branch+rejoin pattern). NO Fork claims these. Pre-G455 they
  //      were dropped here unconditionally, silently breaking layout:
  //      flatToIR omitted them from the IR, ELK never positioned them,
  //      and W133 had to surface them post-hoc as "Disconnected".
  //
  // The claimedJoins Set is populated by buildFork / buildDynamicFork
  // when their findPairedJoin succeeds. Anything not in that set is a
  // standalone join and should render as a regular leaf so the
  // downstream layout sees the convergence point and routes edges to it.
  if (isForkJoinType(t)) {
    if (ctx.claimedJoins.has(node.id)) return null; // absorbed into a Fork
    return buildLeaf(ctx, node); // standalone — emit so layout sees it
  }

  // ── Cycle / depth guards (#37) ───────────────────────────────────
  // If we're already materializing this node id higher up in the
  // descent, the input has a structural cycle (e.g. case-head reaches
  // back into the enclosing branch). Without this guard the
  // walkSequence + buildSwitch / buildFork mutual recursion can blow
  // the JS stack on a small DAG (we observed it on 16-node GEL
  // enforcement DAGs). Degrading to a Leaf preserves the rest of the
  // layout — the user still sees and can edit every other node.
  if (ctx.descentDepth >= ctx.descentDepthMax) {
    ctx.cycleGuardHits.push(node.id);
    return cycleGuardLeaf(ctx, node, 'depth');
  }
  ctx.descent.add(node.id);
  ctx.descentDepth += 1;
  try {
    if (isSwitchType(t)) return buildSwitch(ctx, node, outerStopAt);
    if (isForkType(t))   return buildFork(ctx, node);
    if (isLoopType(t))   return buildDoWhile(ctx, node);
    if (isSubWorkflowType(t)) return buildSubWorkflow(ctx, node);

    // Terminate / End-typed node:
    //  - `end` with config.status=failed → Terminate
    //  - `end` with config.status=completed on a branch (not the trunk tail)
    //    → Terminate (surfaces to the caller; trunk tail handled in flatToIR)
    //  - explicit `terminate` → Terminate
    if (isTerminateType(t)) return buildTerminate(ctx, node);
    if (isEndSentinelCandidate(node, ctx)) {
      // This is the trunk tail — End sentinel is returned by the top-level
      // flatToIR, so as a materialized node inside a sequence we treat it as
      // a Terminate (unreachable in well-formed input, but defensive).
      return buildTerminate(ctx, node);
    }

    // C1 — a gate with two or more of its declared ports wired is a
    // conditional, and renders as one. Checked after the container types so
    // nothing that already owns its multi-out semantics can reach here, and
    // before the leaf fallback because that fallback is where every arm but
    // one used to be lost.
    const gateArms = detectGateArms(ctx, node);
    if (gateArms) return buildGateSwitch(ctx, node, gateArms, outerStopAt);

    // Everything else is a leaf.
    return buildLeaf(ctx, node);
  } finally {
    ctx.descent.delete(node.id);
    ctx.descentDepth -= 1;
  }
}

/**
 * Degrade a cycle-guard hit to a labelled Leaf. The label carries the
 * reason ("circular" or "depth") so the canvas surfaces a clear stub
 * instead of silently swallowing the offending subtree. Operators see
 * the stub on the canvas and the cycleGuardHits set in the console
 * warning, which together pinpoint which materializer is emitting the
 * back-edge.
 */
function cycleGuardLeaf(ctx, node, reason) {
  /* The stub stands FOR a node; it is not that node. The real one is
   * already emitted at its proper place in the tree — that is what made
   * this a cycle — so reusing its id would put the same id in the tree
   * twice and leave the layout positioning two cards for one step. A
   * derived id keeps the stub distinct, and reserveSyntheticId keeps it
   * clear of anything the operator authored. */
  const stubId = reserveSyntheticId(ctx, `${node.id}__cycle_${++ctx.cycleStubSeq}__`);
  const baseLabel = displayLabel(node) || node.id;
  const hint = reason === 'circular'
    ? '⚠ circular reference'
    : '⚠ excessive nesting';
  return makeLeaf({
    id: stubId,
    label: `${baseLabel} (${hint})`,
    canvasType: normalizeCanvasType(node.type),
    props: node.config ? { ...node.config, __cycleGuard: reason } : { __cycleGuard: reason },
  });
}

function buildLeaf(ctx, node) {
  return makeLeaf({
    id: node.id,
    label: displayLabel(node),
    canvasType: normalizeCanvasType(node.type),
    props: node.config ? { ...node.config } : {},
  });
}

function buildTerminate(ctx, node) {
  const cfg = node.config || {};
  const status = typeof cfg.status === 'string' ? cfg.status : 'COMPLETED';
  const reason = typeof cfg.reason === 'string' && cfg.reason.length > 0
    ? cfg.reason
    : (displayLabel(node) || 'Terminated');
  return makeTerminate({
    id: node.id,
    label: displayLabel(node) || 'Terminate',
    reason,
    status: status.toUpperCase(),
  });
}

function buildSubWorkflow(ctx, node) {
  const cfg = node.config || {};
  const refName = cfg.subWorkflowName || cfg.name || displayLabel(node);
  const refVersion = typeof cfg.subWorkflowVersion === 'number' ? cfg.subWorkflowVersion : undefined;
  const async = !!cfg.async;
  return makeSubWorkflow({
    id: node.id,
    label: displayLabel(node),
    refName,
    refVersion,
    async,
    embedded: null,
  });
}

// ───────────────────────────────────────────────────────────────────
//  Switch
// ───────────────────────────────────────────────────────────────────

function buildSwitch(ctx, switchNode, outerStopAt = null) {
  const outs = ctx.outgoing.get(switchNode.id) || [];

  // Switch expression source: prefer the canonical `config.expression` but
  // fall back to legacy / AI-emitted aliases. The Canvas-AI system prompt
  // historically described the field as `switchExpression`, so workflows
  // generated through that pipeline carry the expression under that key —
  // reading both keeps those workflows lintable until they're rewritten.
  // `condition` covers a separate older alias that the conductor importer
  // sometimes leaves behind for if/else nodes.
  const swCfg = switchNode.config || {};
  const swExpr =
    (typeof swCfg.expression === 'string' && swCfg.expression)
    || (typeof swCfg.switchExpression === 'string' && swCfg.switchExpression)
    || (typeof swCfg.conditionExpression === 'string' && swCfg.conditionExpression)
    || (typeof swCfg.condition === 'string' && swCfg.condition)
    || '';

  // canvasType stays null: the emitter's existing default is `condition`,
  // which is what every switch draws with today.
  return buildArmedSwitch(ctx, switchNode, outs, outerStopAt, { expr: swExpr });
}

/**
 * C1 — Build the Switch IR for a gate.
 *
 * Same structure as a condition node, because it is the same claim about
 * execution: one arm runs, chosen at runtime. The two differences are that a
 * gate picks its arm from a verdict rather than from an expression, and that
 * it must keep its own chrome — an operator looking for the Data Protection
 * Gate on their canvas should find a Data Protection Gate, not a diamond.
 */
function buildGateSwitch(ctx, gateNode, arms, outerStopAt = null) {
  return buildArmedSwitch(ctx, gateNode, arms, outerStopAt, {
    // A gate has no expression to show. makeSwitch already accepts an empty
    // one — the relaxation written for half-configured switches.
    expr: '',
    canvasType: gateNode.type,
  });
}

/**
 * The arm-walking machinery shared by a condition node and a gate.
 *
 * Extracted rather than copied: the post-dominator search, the G485 barrier
 * inheritance and the G480 duplicate-handle disambiguation below are the
 * hard-won parts, and a second copy of them is exactly the drift that put
 * three different answers to "is this branch wired" in three validators.
 */
function buildArmedSwitch(ctx, switchNode, outs, outerStopAt, { expr = '', canvasType = null }) {
  // Compute the post-dominator (the node where all arms reconverge).
  const armHeads = outs.map(c => c.target);
  const postDom = computePostDominator(ctx, switchNode.id, armHeads);

  // G485 — Inherit the enclosing barrier when this switch has no
  // post-dominator of its own. computePostDominator returns null when an
  // arm terminates (e.g. an approval_check whose .false → TERMINATE and
  // .true → the shared downstream tail). Without a barrier that .true arm's
  // walkSequence runs with stopAt=null and swallows the ENTIRE shared tail
  // into this one case; the sibling arms then dedup to empty stubs via the
  // G483 guard, rendering as a massively-wide, lopsided switch (the
  // medication-severity workflow: one branch held assign→fork→audit→end,
  // the others held empty fork stubs → ~5500px wide). Falling back to the
  // caller's stopAt (the OUTER switch's post-dominator) makes every arm
  // stop at the shared convergence node, so the tail is emitted ONCE after
  // the switch. Mirrors buildImplicitFork's `postDomId || outerStopAt` (G458).
  const armStopAt = postDom || outerStopAt || null;

  // Build each Case by following its arm from `c.target` up to (but not
  // including) `postDom`. `sourceHandle` becomes the case name; the
  // canonical `default` handle marks the default arm.
  //
  // G480 — Duplicate-handle disambiguation.
  //
  // Some materializer outputs emit MULTIPLE outbounds from a single switch
  // sharing the same sourceHandle (most commonly: a Conductor `decision`
  // task whose `defaultCase` array contains more than one task ref gets
  // emitted as two `default`-port edges). Pre-G480 the second iteration
  // overwrote `defaultCase = caseObj`, silently dropping the first arm's
  // children from the IR. That arm's nodes then surfaced as W133
  // disconnected orphans (Cf. insurance_claim v44's `n_terminate_ref`
  // which lost its arm to terminate_ref_2 winning the overwrite).
  //
  // Fix: when we encounter a handle we've already seen, suffix it
  // (`default_2`, `yes_3`, …) so each outbound becomes a distinct case
  // object. Only the FIRST `default`-handle arm keeps `isDefault: true` —
  // subsequent same-handle arms are demoted to regular cases (their suffix
  // differentiates them visually). The IR Switch contract stays valid:
  // each arm has a unique name, each renders with its own chrome.
  const cases = [];
  let defaultCase = null;
  const seenHandles = new Map(); // handle → count, used to suffix dupes
  for (const c of outs) {
    const rawHandle = typeof c.sourceHandle === 'string' && c.sourceHandle.length > 0 ? c.sourceHandle : 'default';
    const seenCount = seenHandles.get(rawHandle) || 0;
    seenHandles.set(rawHandle, seenCount + 1);
    const handle = seenCount === 0 ? rawHandle : `${rawHandle}_${seenCount + 1}`;
    // First `default`-handle arm keeps isDefault; later duplicates do not.
    const isDefault = rawHandle === 'default' && seenCount === 0;
    const armHead = c.target;
    let children = [];
    if (armStopAt !== armHead) {
      const { items } = walkSequence(ctx, armHead, armStopAt, null);
      children = items;
    }
    // If the arm head IS the postDom, the case is empty (direct reconvergence).
    const caseObj = makeCase({ name: handle, isDefault, children });
    if (isDefault) defaultCase = caseObj;
    else cases.push(caseObj);
  }

  return makeSwitch({
    id: switchNode.id,
    label: displayLabel(switchNode),
    expr,
    cases,
    defaultCase,
    canvasType,
  });
}

// ───────────────────────────────────────────────────────────────────
//  Fork / DynamicFork
// ───────────────────────────────────────────────────────────────────

function buildFork(ctx, forkNode) {
  const outs = ctx.outgoing.get(forkNode.id) || [];
  const joinNode = findPairedJoin(ctx, forkNode, outs);
  if (!joinNode) {
    // Mid-edit recovery — the user just dropped a Fork onto the canvas
    // and hasn't yet wired its branches into a parallel_join. Throwing
    // here blanks the entire layout and surfaces a scary red banner;
    // instead, degrade this Fork to a labelled leaf with a "(⚠ unpaired
    // fork)" hint so the rest of the DAG keeps rendering and the user
    // can finish wiring the join. Mirrors the cycleGuardLeaf recovery
    // pattern already used for circular / over-deep IR.
    // eslint-disable-next-line no-console
    console.warn(
      '[flatToIR] Fork ' + forkNode.id + ' has no paired parallel_join '
      + 'reachable from all branches. Rendering the fork as a placeholder '
      + 'leaf so the rest of the canvas stays visible. Wire every branch '
      + 'to a parallel_join (or terminate every branch with TERMINATE) to '
      + 'restore the full Fork layout.'
    );
    const baseLabel = displayLabel(forkNode) || forkNode.id;
    return makeLeaf({
      id: forkNode.id,
      label: `${baseLabel} (⚠ unpaired fork)`,
      canvasType: normalizeCanvasType(forkNode.type),
      props: forkNode.config
        ? { ...forkNode.config, __forkGuard: 'unpaired' }
        : { __forkGuard: 'unpaired' },
    });
  }

  // G455 — claim this join so materializeNode knows to absorb it (return
  // null) rather than emitting it as a standalone leaf. Done BEFORE
  // walking the branches because nested forks inside a branch may also
  // reference this join during their own findPairedJoin walks; claiming
  // up-front prevents a misfire if a deep nested arm happens to visit
  // this join via materializeNode before our walk completes.
  ctx.claimedJoins.add(joinNode.id);

  const branches = outs
    .filter(c => c.target !== joinNode.id)
    .map(c => {
      const handle = typeof c.sourceHandle === 'string' && c.sourceHandle.length > 0 ? c.sourceHandle : 'default';
      const { items } = walkSequence(ctx, c.target, joinNode.id, null);
      return makeBranch({ name: handle, children: items });
    });

  const joinOn = branches.map(b => b.name);
  const joinCfg = joinNode.config || {};
  const waitMode =
    joinCfg.joinMode === 'ANY_COMPLETED' || joinCfg.joinMode === 'ANY'
      ? 'ANY'
      : 'ALL';
  const join = makeJoin({
    id: joinNode.id,
    label: displayLabel(joinNode) || 'Join',
    joinOn,
    waitMode,
  });

  const isDynamic = !!(forkNode.config && forkNode.config.isDynamic);
  if (isDynamic) {
    return makeDynamicFork({
      id: forkNode.id,
      label: displayLabel(forkNode),
      resolvedBranches: branches.length > 0 ? branches : null,
      join,
    });
  }
  return makeFork({
    id: forkNode.id,
    label: displayLabel(forkNode),
    branches,
    join,
  });
}

/**
 * Find the paired parallel_join for a Fork by BFS over default successors
 * of the fork's branch heads, stopping at the first parallel_join that is
 * reachable from all of them.
 */
function findPairedJoin(ctx, forkNode, outs) {
  const branchHeads = outs.map(c => c.target);
  const reachableSets = branchHeads.map(h => reachableIgnoringLoopBack(ctx, h));
  if (reachableSets.length === 0) return null;
  // Intersection
  const common = new Set(reachableSets[0]);
  for (let i = 1; i < reachableSets.length; i++) {
    const s = reachableSets[i];
    for (const id of [...common]) if (!s.has(id)) common.delete(id);
  }
  // Pick the join type in the common set, closest to the fork.
  // Closeness by BFS from the fork itself.
  const bfsOrder = bfsOrderIgnoringLoopBack(ctx, forkNode.id);
  for (const id of bfsOrder) {
    if (!common.has(id)) continue;
    const n = ctx.nodes.get(id);
    if (n && isForkJoinType(n.type)) return n;
  }
  return null;
}

// ───────────────────────────────────────────────────────────────────
//  DoWhile (loop)
// ───────────────────────────────────────────────────────────────────

function buildDoWhile(ctx, loopNode) {
  const outs = ctx.outgoing.get(loopNode.id) || [];
  // body-start = the body out-edge; loop continues until loop_back arrives
  // at the loopNode; exit = the exit out-edge (see advanceAfter).
  //
  // Canonical spelling first, alias second. The order is the zero-regression
  // guarantee: any loop that has a `default` out-edge resolves exactly as it
  // did before, so every graph that rendered yesterday is untouched. Only a
  // loop with NO canonical body edge reaches the fallback — and those
  // rendered an empty body, so there is no prior behaviour to preserve.
  const defaultOut = outs.find(c => (c.sourceHandle || 'default') === LOOP_BODY_HANDLE)
                  || outs.find(c => isLoopBodyHandle(c.sourceHandle));

  let body = [];
  // Prefer the explicit `_loopBody` hint (emitted by the backend converter
  // for DO_WHILE — see containerHint.js §LEGACY_LOOP_BODY_FIELD). When the
  // canvas annotates body membership this way, we MUST honor it, because:
  //   1. Payloads from the Conductor converter often omit a `loop_back`
  //      out-edge entirely, so the "walk until we return to loopNode"
  //      heuristic over-collects the entire downstream DAG into the body.
  //   2. A stray reconvergence edge from an outer switch's convergence
  //      pill back into a body node (malformed but seen in the wild)
  //      makes the plain edge-walk pull siblings into the body.
  // Metadata is authoritative when present.
  const explicitBody = Array.isArray(loopNode._loopBody) && loopNode._loopBody.length > 0
    ? loopNode._loopBody
    : null;
  if (explicitBody) {
    const bodySet = new Set(explicitBody);
    // Find a body root — a body node with no incoming edges from another
    // body node. Fall back to the first listed id.
    const bodyRoot = explicitBody.find(id => {
      const ins = (ctx.incoming.get(id) || []).filter(c => bodySet.has(c.source));
      return ins.length === 0;
    }) || explicitBody[0];
    // Walk the body, stopping as soon as we step outside the body set.
    // Announce that this loop is collecting its own body, so the
    // reservation check in materializeNode lets its nodes through.
    const previousOwner = ctx.buildingBodyFor;
    ctx.buildingBodyFor = loopNode.id;
    try {
      const { items } = walkSequence(
        ctx,
        bodyRoot,
        /* stopAt */ null,
        /* exitPredicate */ (n) => !bodySet.has(n.id)
      );
      body = items;
    } finally {
      ctx.buildingBodyFor = previousOwner;
    }
  } else if (defaultOut) {
    const { items } = walkSequence(ctx, defaultOut.target, /*stopAt*/ loopNode.id, null);
    body = items;
  }

  const cfg = loopNode.config || {};
  const condition = typeof cfg.loopCondition === 'string' && cfg.loopCondition.length > 0
    ? cfg.loopCondition
    : '${true}';
  const iterationHint = typeof cfg.iterationHint === 'string' ? cfg.iterationHint : undefined;

  // Stash the exit target on the IR node so the top-level walker can jump
  // over the loop. We do this by making `advanceAfter` for a loop return
  // the exit edge's target.
  return makeDoWhile({
    id: loopNode.id,
    label: displayLabel(loopNode),
    body,
    condition,
    iterationHint,
  });
}

// ───────────────────────────────────────────────────────────────────
//  Sequence advancement — where does `node` hand off to?
// ───────────────────────────────────────────────────────────────────

/**
 * Given we just emitted `node` into a sequence, return the next node to
 * continue from. For scalars: default out-edge target. For containers
 * (Switch, Fork, Loop): the container's post-exit on the enclosing trunk.
 */
function advanceAfter(ctx, node) {
  const t = normalizeCanvasType(node.type);
  if (isSwitchType(t)) {
    const outs = ctx.outgoing.get(node.id) || [];
    const armHeads = outs.map(c => c.target);
    const postDom = computePostDominator(ctx, node.id, armHeads);
    return postDom ? ctx.nodes.get(postDom) : null;
  }
  if (isForkType(t)) {
    const outs = ctx.outgoing.get(node.id) || [];
    const join = findPairedJoin(ctx, node, outs);
    if (!join) return null;
    // G455 — defensive idempotent claim. buildFork normally claims the
    // paired join first (so materializeNode absorbs it), but advanceAfter
    // can be invoked from walkSequence for a fork that's already been
    // materialized; double-add on a Set is a no-op.
    ctx.claimedJoins.add(join.id);
    return singleDefaultSuccessor(ctx, join.id);
  }
  if (isLoopType(t)) {
    const outs = ctx.outgoing.get(node.id) || [];
    // Canonical spelling first, alias second — same zero-regression ordering
    // as buildDoWhile. A loop carrying an `exit` edge behaves exactly as
    // before; only a loop that previously had no resolvable exit — and so
    // dead-ended the walk, dropping every node after it — reaches the alias.
    const exitOut = outs.find(c => c.sourceHandle === LOOP_EXIT_HANDLE)
                 || outs.find(c => isLoopExitHandle(c.sourceHandle));
    if (exitOut) return ctx.nodes.get(exitOut.target);
    // No explicit exit: take whichever default out-edge leaves the loop head
    // (i.e. not the body start — but we can't distinguish without knowing the
    // body). Conservatively return null.
    return null;
  }
  // C1 — a gate's arms are its cases, so the trunk resumes where they
  // reconverge. Identical to the switch path above; `singleDefaultSuccessor`
  // would answer with whichever arm happened to be first in the array, which
  // is how the DPG's `blocked` path and the human task's `rejected` path came
  // to be swallowed into the trunk instead of rendered as alternatives.
  const gateArms = detectGateArms(ctx, node);
  if (gateArms) {
    const postDom = computePostDominator(ctx, node.id, gateArms.map(c => c.target));
    return postDom ? ctx.nodes.get(postDom) : null;
  }
  return singleDefaultSuccessor(ctx, node.id);
}

// G267 — sourceHandle values that are NOT part of the structural trunk walk.
// `loop_back` is the DoWhile iteration back-edge; `cognitive_avail` and
// `cognitive_return` are the cognitive-loop fan-out availability / return
// edges (see AGENT_EXECUTION_MODEL.md §4). flatToIR ignores all three when
// picking the next trunk node so cognitive_loop nodes — which have N+1
// outgoing edges (N availability + 1 trunk-to-tail) — don't trip the
// "ambiguous successor" guard.
const AUX_SOURCE_HANDLES = new Set(['loop_back', 'cognitive_avail', 'cognitive_return']);

/**
 * The edges that carry structural flow out of a node.
 *
 * One definition, used by the entry hop and the gate detector, which each
 * kept their own copy of the same filter. The entry hop did not filter at
 * all — it asked `singleDefaultSuccessor` for one node and threw the rest
 * away, which is how the Start came to be the only node in the graph whose
 * extra successors were discarded.
 *
 * `detectImplicitFanOut` deliberately does NOT use this: it asks the
 * narrower question "more than one edge on the `default` handle", because
 * two edges on DIFFERENT handles are a gate (exactly one arm runs), not a
 * parallel fan-out. `singleDefaultSuccessor` keeps its own prefer-default
 * ordering for the same reason — a loop or fork head's trunk continuation
 * is its `default` edge specifically.
 */
function structuralOuts(ctx, nodeId) {
  return (ctx.outgoing.get(nodeId) || [])
    .filter((c) => !AUX_SOURCE_HANDLES.has(c.sourceHandle));
}

function singleDefaultSuccessor(ctx, id) {
  const outs = ctx.outgoing.get(id) || [];
  // Prefer handle === 'default'; fall back to the unique non-auxiliary out-edge.
  const def = outs.find(c => (c.sourceHandle || 'default') === 'default');
  if (def) return ctx.nodes.get(def.target);
  const nonAux = outs.filter(c => !AUX_SOURCE_HANDLES.has(c.sourceHandle));
  if (nonAux.length === 1) return ctx.nodes.get(nonAux[0].target);
  return null;
}

// ───────────────────────────────────────────────────────────────────
//  Post-dominator / reachability (DAG, ignoring loop_back edges)
// ───────────────────────────────────────────────────────────────────

function reachableIgnoringLoopBack(ctx, startId) {
  const seen = new Set();
  const stack = [startId];
  while (stack.length) {
    const id = stack.pop();
    if (seen.has(id)) continue;
    seen.add(id);
    const outs = ctx.outgoing.get(id) || [];
    for (const c of outs) {
      if (c.sourceHandle === 'loop_back') continue;
      stack.push(c.target);
    }
  }
  return seen;
}

function bfsOrderIgnoringLoopBack(ctx, startId) {
  const seen = new Set();
  const order = [];
  const queue = [startId];
  while (queue.length) {
    const id = queue.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    order.push(id);
    const outs = ctx.outgoing.get(id) || [];
    for (const c of outs) {
      if (c.sourceHandle === 'loop_back') continue;
      if (!seen.has(c.target)) queue.push(c.target);
    }
  }
  return order;
}

/**
 * Compute the immediate post-dominator of `containerId` given its arm
 * heads: the nearest node (in BFS order from containerId, ignoring
 * loop_back) that is reachable from every CONVERGING arm head, and is not
 * containerId itself.
 *
 * Arms whose path dead-ends in a Terminate / failed-End are EXCLUDED from
 * the intersection. A terminating arm never rejoins the trunk, so it
 * should not constrain the convergence point. (Example: a SWITCH whose
 * `default` case reject-terminates while the other cases converge at a
 * merge node — the merge IS the post-dom for the surviving arms.)
 *
 * An arm is considered "terminating" when its reachable set (excluding
 * the head itself) shares no nodes with any other arm's reachable set.
 *
 * Returns the post-dominator node id, or null if no convergence exists
 * (e.g. all arms terminate, or the arms form disjoint trunks).
 */
function computePostDominator(ctx, containerId, armHeads) {
  if (armHeads.length === 0) return null;
  const reach = armHeads.map(h => reachableIgnoringLoopBack(ctx, h));

  // Identify non-terminating arms — those whose reach (minus the head)
  // shares at least one node with some other arm's reach (minus the head).
  // Arms that share no convergence node with any other are terminating.
  const convergingIdxs = [];
  for (let i = 0; i < reach.length; i++) {
    const mine = new Set(reach[i]);
    mine.delete(armHeads[i]);
    let sharesWithOther = false;
    for (let j = 0; j < reach.length; j++) {
      if (j === i) continue;
      for (const id of mine) {
        if (reach[j].has(id)) { sharesWithOther = true; break; }
      }
      if (sharesWithOther) break;
    }
    if (sharesWithOther) convergingIdxs.push(i);
  }

  // If zero or one converging arm, there's no multi-way convergence point.
  // (A single converging arm has no post-dom to pin down — its continuation
  // is the arm's natural trunk.)
  if (convergingIdxs.length < 2) return null;

  // Intersection of reachable-sets across converging arms only, excluding
  // containerId itself.
  const convergingReach = convergingIdxs.map(i => reach[i]);
  const common = new Set(convergingReach[0]);
  common.delete(containerId);
  for (let k = 1; k < convergingReach.length; k++) {
    for (const id of [...common]) if (!convergingReach[k].has(id)) common.delete(id);
  }
  if (common.size === 0) return null;
  const order = bfsOrderIgnoringLoopBack(ctx, containerId);
  for (const id of order) {
    if (common.has(id)) return id;
  }
  return null;
}

// ───────────────────────────────────────────────────────────────────
//  Helpers
// ───────────────────────────────────────────────────────────────────

function displayLabel(node) {
  return typeof node.label === 'string' && node.label.length > 0 ? node.label : node.id;
}

/**
 * Is `node` a candidate End sentinel? We treat a node as the End if:
 *   - type is 'end' (canonical), AND
 *   - it has no outgoing edges (terminal), AND
 *   - status is not explicitly `failed` (which would indicate Terminate).
 */
function isEndSentinelCandidate(node, ctx) {
  if (!isEndType(normalizeCanvasType(node.type))) return false;
  const outs = ctx.outgoing.get(node.id) || [];
  if (outs.length > 0) return false;
  const status = (node.config && typeof node.config.status === 'string') ? node.config.status : '';
  if (status.toLowerCase() === 'failed') return false;
  return true;
}

// Silence unused-variable warnings for re-exported kind helpers that may be
// removed after migration without touching this file.
/* eslint-disable no-unused-vars */
const _unused = { IR_KIND, CANVAS_TYPE, isJoinType };
/* eslint-enable no-unused-vars */
